/**
 * Configurable dashboard cards.
 *
 * The fixed operational panels below these are the *default* view and stay where they are: they
 * answer "is anything on fire, is the platform seeing anything, is the platform itself broken",
 * which is what anybody arriving needs first. What this adds is the workspace's own cards above
 * them — the four things this particular team watches.
 *
 * An ordered list, not a canvas. A drag-and-drop grid needs collision handling, responsive reflow
 * and a stored geometry that goes wrong on a narrower screen; an ordered list of typed cards
 * reflows correctly everywhere and cannot be arranged into a broken state. That is a real
 * limitation and it is written down rather than implied away.
 */

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  api,
  GRID_COLUMNS,
  type Coverage,
  type Placement,
  type Dashboard,
  type DashboardCard,
  type QueryResult,
  type SavedQuery,
  type VolumeResponse,
} from '../api'
import { useSession } from '../App'
import { Card, Chip, Empty, Modal, Note, Stat, fmtBytes, fmtNumber, useAsync, useToast } from './ui'

export default function CustomDashboard() {
  const [arranging, setArranging] = useState(false)
  const toast = useToast()
  const { can, me } = useSession()
  const dashboards = useAsync<Dashboard[]>(() => api.get<Dashboard[]>('/dashboards'), [me.tenant])
  const queries = useAsync<SavedQuery[]>(() => api.get<SavedQuery[]>('/queries'), [me.tenant])
  const [selected, setSelected] = useState<string | null>(null)
  const [editing, setEditing] = useState<Dashboard | 'new' | null>(null)

  const all = dashboards.data ?? []
  // The marked default, else the first by id. Deterministic either way, so two people opening the
  // console see the same thing rather than whatever the map iterated first.
  const current =
    all.find((d) => d.id === selected) ??
    all.find((d) => d.is_default) ??
    [...all].sort((a, b) => a.id.localeCompare(b.id))[0]

  if (all.length === 0 && !can('write_incidents')) return null

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div className="row wrap" style={{ gap: 6 }}>
          {all.map((d) => (
            <button
              key={d.id}
              className={current?.id === d.id ? 'primary sm' : 'sm'}
              onClick={() => setSelected(d.id)}
            >
              {d.name}
              {d.is_default && <span className="faint"> · default</span>}
            </button>
          ))}
        </div>
        {can('write_incidents') && (
          <div className="row" style={{ gap: 6 }}>
            {current && (
              <button className="sm" onClick={() => setEditing(current)}>
                Edit
              </button>
            )}
            {current && current.cards.length > 0 && !arranging && (
              <button className="sm" onClick={() => setArranging(true)}>
                Arrange
              </button>
            )}
            <button className="sm" onClick={() => setEditing('new')}>
              New dashboard
            </button>
          </div>
        )}
      </div>

      {current && current.cards.length > 0 && !arranging && (
        <DashboardGrid cards={current.cards} queries={queries.data ?? []} />
      )}

      {current && arranging && (
        <ArrangeGrid
          cards={current.cards}
          queries={queries.data ?? []}
          onCancel={() => setArranging(false)}
          onSave={(cards) => {
            api
              .post<Dashboard>('/dashboards', {
                id: current.id,
                name: current.name,
                description: current.description,
                cards,
                is_default: current.is_default,
              })
              .then(() => {
                toast('ok', 'Arrangement saved.')
                setArranging(false)
                dashboards.reload()
              })
              .catch((e: Error) => toast('error', e.message))
          }}
        />
      )}

      {editing && (
        <EditDashboard
          dashboard={editing === 'new' ? null : editing}
          queries={queries.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={(id) => {
            setSelected(id)
            dashboards.reload()
          }}
        />
      )}
    </>
  )
}

/**
 * The arranged view of a dashboard.
 *
 * # Why the geometry is ignored on a narrow screen
 *
 * A stored arrangement is a decision made at one width. Honoring it at every width is what makes a
 * canvas go wrong on a phone - a three-column row becomes three unreadable slivers. So below the
 * breakpoint the geometry is dropped entirely and the cards stack in list order, which is exactly
 * what this dashboard was before it could be arranged. Nothing is stored per breakpoint, so there
 * is no second layout to keep correct.
 *
 * # Why this reimplements nothing
 *
 * The resolved position of every card - including cards with no placement - comes from the same
 * rule the server validates against: placed cards keep their placement, unplaced ones go below
 * everything, full width, in list order. It is repeated here rather than fetched because it is four
 * lines; if it grows, it should move to the API rather than diverge.
 */
function DashboardGrid({
  cards,
  queries,
}: {
  cards: DashboardCard[]
  queries: SavedQuery[]
}) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 900,
  )
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const arranged = cards.some((c) => c.placement)
  if (narrow || !arranged) {
    // The list this has always been. On a narrow screen that is not a degraded view, it is the
    // correct one.
    return (
      <div className="grid cols-2" style={{ marginBottom: 16 }}>
        {cards.map((c) => (
          <CardView key={c.id} card={c} queries={queries} />
        ))}
      </div>
    )
  }

  let nextRow = Math.max(
    0,
    ...cards.filter((c) => c.placement).map((c) => c.placement!.y + c.placement!.h),
  )
  const placed = cards.map((c) => {
    if (c.placement) return { card: c, at: c.placement }
    const at = { x: 0, y: nextRow, w: GRID_COLUMNS, h: 1 }
    nextRow += 1
    return { card: c, at }
  })

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
        gridAutoRows: 'minmax(140px, auto)',
        gap: 12,
        marginBottom: 16,
      }}
    >
      {placed.map(({ card, at }) => (
        <div
          key={card.id}
          style={{
            gridColumn: `${at.x + 1} / span ${at.w}`,
            gridRow: `${at.y + 1} / span ${at.h}`,
            minWidth: 0,
          }}
        >
          <CardView card={card} queries={queries} />
        </div>
      ))}
    </div>
  )
}

const ROW_PX = 140
const GAP_PX = 12

/** Do two placements share a cell? The same rule the server validates with. */
function overlaps(a: Placement, b: Placement): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

/**
 * Arrange cards on the grid by dragging.
 *
 * # Why an explicit mode rather than always-draggable
 *
 * A dashboard is mostly read, not edited. Cards that move when you meant to select text are a
 * hazard on a screen somebody is using during an incident, so arranging is something you turn on.
 *
 * # Why an overlapping drop is refused rather than resolved
 *
 * Pushing neighbors out of the way is the behavior that makes grid editors feel unpredictable:
 * one drag rearranges things you did not touch. A drop that would overlap simply does not commit,
 * and the card shows it. That also keeps the console's rule identical to the validator's, so a
 * dashboard can never be arranged into a state the server would refuse.
 */
function ArrangeGrid({
  cards,
  queries,
  onCancel,
  onSave,
}: {
  cards: DashboardCard[]
  queries: SavedQuery[]
  onCancel: () => void
  onSave: (cards: DashboardCard[]) => void
}) {
  // Every card gets a concrete placement to start from, using the same resolution rule as the
  // read-only grid: unplaced cards go below everything, full width, in list order.
  const initial = (): Record<string, Placement> => {
    let nextRow = Math.max(
      0,
      ...cards.filter((c) => c.placement).map((c) => c.placement!.y + c.placement!.h),
    )
    const out: Record<string, Placement> = {}
    for (const c of cards) {
      if (c.placement) out[c.id] = { ...c.placement }
      else out[c.id] = { x: 0, y: nextRow++, w: GRID_COLUMNS, h: 1 }
    }
    return out
  }

  const [at, setAt] = useState<Record<string, Placement>>(initial)
  const [blocked, setBlocked] = useState<string | null>(null)
  const gridRef = useRef<HTMLDivElement | null>(null)
  const drag = useRef<{
    id: string
    mode: 'move' | 'resize'
    startX: number
    startY: number
    from: Placement
  } | null>(null)

  const cellWidth = () => {
    const rect = gridRef.current?.getBoundingClientRect()
    if (!rect) return 1
    return (rect.width - GAP_PX * (GRID_COLUMNS - 1)) / GRID_COLUMNS + GAP_PX
  }

  function begin(e: React.PointerEvent, id: string, mode: 'move' | 'resize') {
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    const from = at[id]
    if (!from) return
    drag.current = { id, mode, startX: e.clientX, startY: e.clientY, from: { ...from } }
  }

  function move(e: React.PointerEvent) {
    const d = drag.current
    if (!d) return
    const dx = Math.round((e.clientX - d.startX) / cellWidth())
    const dy = Math.round((e.clientY - d.startY) / (ROW_PX + GAP_PX))

    let next: Placement
    if (d.mode === 'move') {
      next = {
        ...d.from,
        x: Math.min(Math.max(0, d.from.x + dx), GRID_COLUMNS - d.from.w),
        y: Math.max(0, d.from.y + dy),
      }
    } else {
      next = {
        ...d.from,
        w: Math.min(Math.max(1, d.from.w + dx), GRID_COLUMNS - d.from.x),
        h: Math.max(1, d.from.h + dy),
      }
    }

    const clash = Object.entries(at).some(([id, p]) => id !== d.id && overlaps(next, p))
    setBlocked(clash ? d.id : null)
    if (!clash) setAt((prev) => ({ ...prev, [d.id]: next }))
  }

  function end() {
    drag.current = null
    setBlocked(null)
  }

  return (
    <>
      <div className="row" style={{ gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <Chip tone="warn">arranging</Chip>
        <span className="faint small">
          Drag a card to move it, or its bottom-right corner to resize. A move that would overlap
          another card does not take.
        </span>
        <div style={{ flex: 1 }} />
        <button className="sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="sm primary"
          onClick={() => onSave(cards.map((c) => ({ ...c, placement: at[c.id] })))}
        >
          Save arrangement
        </button>
      </div>

      <div
        ref={gridRef}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
          gridAutoRows: `${ROW_PX}px`,
          gap: GAP_PX,
          marginBottom: 16,
          userSelect: 'none',
        }}
      >
        {cards.map((c) => {
          const p = at[c.id]
          if (!p) return null
          return (
            <div
              key={c.id}
              onPointerDown={(e) => begin(e, c.id, 'move')}
              style={{
                gridColumn: `${p.x + 1} / span ${p.w}`,
                gridRow: `${p.y + 1} / span ${p.h}`,
                position: 'relative',
                cursor: 'grab',
                minWidth: 0,
                outline:
                  blocked === c.id ? '2px solid var(--danger)' : '1px dashed var(--border)',
                borderRadius: 8,
              }}
            >
              <CardView card={c} queries={queries} />
              <div
                onPointerDown={(e) => begin(e, c.id, 'resize')}
                title="Resize"
                style={{
                  position: 'absolute',
                  right: 2,
                  bottom: 2,
                  width: 16,
                  height: 16,
                  cursor: 'nwse-resize',
                  borderRight: '3px solid var(--text-faint)',
                  borderBottom: '3px solid var(--text-faint)',
                  borderBottomRightRadius: 6,
                }}
              />
            </div>
          )
        })}
      </div>
    </>
  )
}

/** One card. Every kind renders its own emptiness honestly rather than as a blank box. */
function CardView({ card, queries }: { card: DashboardCard; queries: SavedQuery[] }) {
  switch (card.kind) {
    case 'saved_query':
      return <QueryCard card={card} queries={queries} />
    case 'volume':
      return <VolumeCard card={card} />
    case 'coverage':
      return <CoverageCard title={card.title} />
    case 'incidents_by_severity':
      return <SeverityCard title={card.title} />
    case 'note':
      return (
        <Card title={card.title || 'Note'}>
          <p className="small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
            {card.text}
          </p>
        </Card>
      )
  }
}

function QueryCard({
  card,
  queries,
}: {
  card: Extract<DashboardCard, { kind: 'saved_query' }>
  queries: SavedQuery[]
}) {
  const { me } = useSession()
  const query = queries.find((q) => q.id === card.query_id)
  const result = useAsync<QueryResult>(
    () => api.get<QueryResult>(`/queries/${card.query_id}/run`),
    [me.tenant, card.query_id],
  )
  const rows = (result.data?.rows ?? []).slice(0, card.rows ?? 5)

  return (
    <Card
      title={card.title || query?.name || 'Saved search'}
      subtitle={query ? `${result.data?.matched ?? 0} matching ${query.target}` : undefined}
      actions={<Link to="/explore">Open</Link>}
    >
      {rows.length === 0 ? (
        <Empty title="Nothing matches right now" />
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {rows.map((r, i) => (
            <li key={String(r.id ?? i)} className="small">
              {String(r.title ?? r.message ?? r.id ?? '—')}
            </li>
          ))}
        </ul>
      )}
      {result.data?.truncated && (
        <div className="faint small" style={{ marginTop: 6 }}>
          Showing {rows.length} of {result.data.matched}.
        </div>
      )}
      {result.data?.window_note && (
        <div style={{ marginTop: 8 }}>
          {/* "Nothing matched" and "nothing matched in the window this process still holds" are
              different answers, and only one of them means the estate is quiet. */}
          <Note tone="warn">{result.data.window_note}</Note>
        </div>
      )}
    </Card>
  )
}

function VolumeCard({ card }: { card: Extract<DashboardCard, { kind: 'volume' }> }) {
  const { me } = useSession()
  const days = card.days ?? 14
  const volume = useAsync<VolumeResponse>(
    () => api.get<VolumeResponse>(`/analytics/volume?days=${days}`),
    [me.tenant, days],
  )
  return (
    <Card title={card.title || `Ingest volume — ${days} days`}>
      {volume.data?.unavailable_reason ? (
        <Note>{volume.data.unavailable_reason}</Note>
      ) : (
        <div className="grid cols-2">
          <Stat label="Events" value={fmtNumber(volume.data?.total_events ?? 0)} />
          <Stat label="Stored" value={fmtBytes(volume.data?.total_bytes ?? 0)} />
        </div>
      )}
    </Card>
  )
}

function CoverageCard({ title }: { title?: string }) {
  const { me } = useSession()
  const coverage = useAsync<Coverage>(() => api.get<Coverage>('/coverage'), [me.tenant])
  return (
    <Card title={title || 'Detection coverage'} actions={<Link to="/coverage">Open</Link>}>
      <Stat
        label="Weighted, against the intelligence program"
        value={`${coverage.data?.weighted_coverage_pct ?? 0}%`}
      />
    </Card>
  )
}

function SeverityCard({ title }: { title?: string }) {
  const { me } = useSession()
  const incidents = useAsync<{ severity: string; status: string }[]>(
    () => api.get<{ severity: string; status: string }[]>('/incidents?limit=1000'),
    [me.tenant],
  )
  const open = (incidents.data ?? []).filter((i) => i.status !== 'closed')
  const counts = ['critical', 'high', 'medium', 'low', 'info'].map((s) => ({
    severity: s,
    n: open.filter((i) => i.severity === s).length,
  }))
  return (
    <Card title={title || 'Open incidents by severity'} actions={<Link to="/incidents">Open</Link>}>
      {open.length === 0 ? (
        <Empty title="No open incidents" />
      ) : (
        <div className="row wrap" style={{ gap: 6 }}>
          {counts
            .filter((c) => c.n > 0)
            .map((c) => (
              <Chip key={c.severity} tone={c.severity === 'critical' || c.severity === 'high' ? 'danger' : 'neutral'}>
                {c.severity}: {c.n}
              </Chip>
            ))}
        </div>
      )}
    </Card>
  )
}

/** Add, remove and reorder cards. The server refuses anything that could not render. */
function EditDashboard({
  dashboard,
  queries,
  onClose,
  onSaved,
}: {
  dashboard: Dashboard | null
  queries: SavedQuery[]
  onClose: () => void
  onSaved: (id: string) => void
}) {
  const toast = useToast()
  const [name, setName] = useState(dashboard?.name ?? '')
  const [isDefault, setIsDefault] = useState(dashboard?.is_default ?? false)
  const [cards, setCards] = useState<DashboardCard[]>(dashboard?.cards ?? [])
  const [busy, setBusy] = useState(false)

  /**
   * Append a card, minting its id here.
   *
   * Typed as a function over the union rather than `Omit<DashboardCard, 'id'>`: distributing an
   * Omit across a discriminated union loses the per-variant fields, and the compiler then rejects
   * `days` and `text` as unknown properties.
   */
  function add(card: (id: string) => DashboardCard) {
    setCards((cs) => [...cs, card(`c${Date.now().toString(36)}${cs.length}`)])
  }

  function move(index: number, by: number) {
    setCards((cs) => {
      const next = [...cs]
      const to = index + by
      if (to < 0 || to >= next.length) return cs
      const item = next.splice(index, 1)[0]
      if (!item) return cs
      next.splice(to, 0, item)
      return next
    })
  }

  return (
    <Modal
      title={dashboard ? `Edit ${dashboard.name}` : 'New dashboard'}
      onClose={onClose}
      footer={
        <>
          {dashboard && (
            <button
              onClick={() => {
                api
                  .del(`/dashboards/${dashboard.id}`)
                  .then(() => {
                    toast('ok', `Deleted "${dashboard.name}".`)
                    onSaved('')
                    onClose()
                  })
                  .catch((e: Error) => toast('error', e.message))
              }}
            >
              Delete
            </button>
          )}
          <button onClick={onClose}>Cancel</button>
          <button
            className="primary"
            disabled={busy || !name.trim()}
            onClick={() => {
              setBusy(true)
              api
                .post<Dashboard>('/dashboards', {
                  id: dashboard?.id ?? '',
                  name,
                  cards,
                  is_default: isDefault,
                })
                .then((d) => {
                  toast('ok', `Saved "${d.name}".`)
                  onSaved(d.id)
                  onClose()
                })
                .catch((e: Error) => toast('error', e.message))
                .finally(() => setBusy(false))
            }}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="dash-name">Name</label>
        <input id="dash-name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <label className="row small" style={{ gap: 6 }}>
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
        Open on this dashboard by default
      </label>

      <h4 style={{ marginBottom: 6 }}>Cards</h4>
      {cards.length === 0 && <p className="small muted">No cards yet.</p>}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {cards.map((c, i) => (
          <li key={c.id} className="row" style={{ gap: 6, marginBottom: 4 }}>
            <Chip tone="outline">{c.kind.replace(/_/g, ' ')}</Chip>
            <span className="small">
              {c.kind === 'saved_query'
                ? (queries.find((q) => q.id === c.query_id)?.name ?? c.query_id)
                : c.kind === 'note'
                  ? c.text.slice(0, 40)
                  : ''}
            </span>
            <span style={{ marginLeft: 'auto' }} className="row">
              <button className="sm" onClick={() => move(i, -1)} aria-label="Move up">
                ↑
              </button>
              <button className="sm" onClick={() => move(i, 1)} aria-label="Move down">
                ↓
              </button>
              <button
                className="sm"
                aria-label="Remove"
                onClick={() => setCards((cs) => cs.filter((x) => x.id !== c.id))}
              >
                ×
              </button>
            </span>
          </li>
        ))}
      </ul>

      <h4 style={{ marginBottom: 6 }}>Add a card</h4>
      <div className="row wrap" style={{ gap: 6 }}>
        <button className="sm" onClick={() => add((id) => ({ id, kind: 'coverage' }))}>
          Coverage
        </button>
        <button className="sm" onClick={() => add((id) => ({ id, kind: 'incidents_by_severity' }))}>
          Incidents by severity
        </button>
        <button className="sm" onClick={() => add((id) => ({ id, kind: 'volume', days: 14 }))}>
          Ingest volume
        </button>
        <button
          className="sm"
          onClick={() => {
            const text = window.prompt('Note text')
            if (text?.trim()) add((id) => ({ id, kind: 'note', text }))
          }}
        >
          Note
        </button>
      </div>
      {queries.length > 0 && (
        <>
          <div className="small muted" style={{ marginTop: 8 }}>
            Saved searches:
          </div>
          <div className="row wrap" style={{ gap: 6 }}>
            {queries.map((q) => (
              <button
                key={q.id}
                className="sm"
                onClick={() => add((id) => ({ id, kind: 'saved_query', query_id: q.id, rows: 5 }))}
              >
                {q.name}
              </button>
            ))}
          </div>
        </>
      )}
      {queries.length === 0 && (
        <Note>
          No saved searches yet. Keep one from <Link to="/explore">Explore</Link> and it can go on a
          dashboard.
        </Note>
      )}
    </Modal>
  )
}
